import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaSpinner, FaCoins, FaHistory } from 'react-icons/fa';
import { requestCreditCheckout } from './PaymentPanel';
import MeteredUsagePanel from './MeteredUsagePanel';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatTimestamp = (value) => (value ? new Date(value).toLocaleString() : '-');
const formatPrice = (minor) => `$${((minor || 0) / 100).toFixed(2)}`;

const CreditPanel = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchasing, setPurchasing] = useState('');
  const [purchaseInfo, setPurchaseInfo] = useState('');
  const [packages, setPackages] = useState([]);

  const loadAccount = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/credits/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load credit balance.');
      setAccount(await response.json());
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load credit balance.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Packages come from the server so displayed prices cannot drift from what
  // Stripe will actually charge.
  const loadPackages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/credits/packages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setPackages(Array.isArray(data.packages) ? data.packages : []);
    } catch (err) {
      // Balance still renders; the purchase section just stays hidden.
    }
  }, [token]);

  useEffect(() => { loadAccount(); }, [loadAccount]);
  useEffect(() => { loadPackages(); }, [loadPackages]);

  const handleBuy = async (packageId) => {
    setPurchasing(packageId);
    setError('');
    try {
      const data = await requestCreditCheckout(token, packageId);
      if (data.redirect_to_stripe && data.stripe_url) {
        window.location.href = data.stripe_url;
        return;
      }
      // Development / mock: credits are granted at confirm.
      if (data.payment_intent_id) {
        await fetch(`${API_BASE}/api/payment/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ payment_intent_id: data.payment_intent_id }),
        });
      }
      setPurchaseInfo('Payment received. Your credits are being added.');
      await loadAccount();
    } catch (err) {
      setError(err.message || 'Failed to start checkout.');
    } finally {
      setPurchasing('');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <FaSpinner className="animate-spin w-8 h-8 text-brand-red" />
      </div>
    );
  }

  if (error && !account) {
    return (
      <div className="space-y-4">
        <div className="bg-red-500/20 border border-red-500/30 text-red-500 px-4 py-3 rounded-lg text-sm">{error}</div>
        <button onClick={loadAccount} className="px-4 py-2 bg-brand-red text-white rounded-lg hover:bg-brand-red-dark transition text-sm font-medium">Retry</button>
      </div>
    );
  }

  const freeRemaining = account.free_remaining ?? 0;
  const paidRemaining = account.paid_remaining ?? 0;
  const monthlyGrant = account.monthly_grant || 0;
  // Free-first spending, so what is gone from the grant is what the cycle used.
  const used = Math.max(0, monthlyGrant - freeRemaining);
  const cardClass = `p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800/40 border-gray-700/40' : 'bg-white/60 border-gray-200/60'}`;
  const mutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="space-y-6">
      {error && account && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-500 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}
      {purchaseInfo && (
        <div className="bg-green-500/20 border border-green-500/30 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm">{purchaseInfo}</div>
      )}

      {/* Available balance (primary) */}
      <div className={`p-6 rounded-xl border text-center ${isDarkMode ? 'bg-gradient-to-br from-brand-purple/10 to-brand-red/10 border-gray-700/40' : 'bg-gradient-to-br from-purple-50 to-red-50 border-gray-200/60'}`}>
        <div className="flex items-center justify-center mb-2">
          <FaCoins className="w-5 h-5 text-brand-red mr-2" />
          <span className={`text-sm font-medium ${mutedClass}`}>Available credits</span>
        </div>
        <div className="text-5xl font-bold text-brand-red">{account.available ?? 0}</div>
        <p className={`text-sm mt-3 ${mutedClass}`}>
          Free credits reset on <span className="font-medium">{formatTimestamp(account.period_end)}</span>
        </p>
      </div>

      {/* Free vs paid: one spendable pool, but the free part is what expires. */}
      <div className="grid grid-cols-2 gap-4">
        <div className={cardClass}>
          <div className={`text-xs ${mutedClass}`}>Free this cycle</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {freeRemaining} <span className={`text-sm font-normal ${mutedClass}`}>of {monthlyGrant}</span>
          </div>
          <div className={`text-xs mt-1 ${mutedClass}`}>Resets {formatTimestamp(account.period_end)}</div>
        </div>
        <div className={cardClass}>
          <div className={`text-xs ${mutedClass}`}>Paid credits</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{paidRemaining}</div>
          <div className={`text-xs mt-1 ${mutedClass}`}>Paid credits never expire</div>
        </div>
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className={cardClass}>
          <div className={`text-xs ${mutedClass}`}>Used this cycle</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{used}</div>
        </div>
        <div className={cardClass}>
          <div className={`text-xs ${mutedClass}`}>Reserved</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{account.reserved ?? 0}</div>
        </div>
        <div className={cardClass}>
          <div className={`text-xs ${mutedClass}`}>Offline reserved</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{account.offline_reserved ?? 0}</div>
        </div>
      </div>

      {(account.available ?? 0) <= 0 && (
        <div className={cardClass}>
          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            You&apos;re out of credits.
          </p>
          <p className={`text-sm mt-1 ${mutedClass}`}>
            Buy prepaid credits, or turn on On-Demand Usage to keep working and
            pay only for what you use.
          </p>
        </div>
      )}

      {/* Purchase: fixed packs, one CTA each. Prices come from the server. */}
      {packages.length > 0 && (
        <div className={cardClass + ' space-y-3'}>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Buy credits</h3>
          <p className={`text-sm ${mutedClass}`}>
            Purchased credits are added to your account and never expire.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {packages.map((pack) => (
              <button key={pack.id} onClick={() => handleBuy(pack.id)}
                disabled={!!purchasing}
                className="flex flex-col items-center justify-center py-4 px-3 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg disabled:opacity-50">
                {purchasing === pack.id ? (
                  <><FaSpinner className="animate-spin w-4 h-4 mb-1" />Processing...</>
                ) : (
                  <>
                    <span className="text-lg font-bold">{pack.credits} credits</span>
                    <span className="text-xs opacity-90">{formatPrice(pack.price_minor)}</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* On-Demand metered usage. Renders nothing when the deployment flag is
          off, so this is a no-op for a prepaid-only install. */}
      <MeteredUsagePanel onChanged={loadAccount} />

      <Link to="/credit-usage" className="inline-flex items-center text-sm text-brand-red hover:underline">
        <FaHistory className="w-3.5 h-3.5 mr-2" />View usage &amp; activity
      </Link>
    </div>
  );
};

export default CreditPanel;
